import { FC, ReactNode, useEffect, useRef } from "react";
import { MdClose } from "react-icons/md";
import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 24px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
`;

const pop = keyframes`
  0%   { transform: scale(0.6) rotate(-12deg); }
  60%  { transform: scale(1.15) rotate(6deg); }
  100% { transform: scale(1) rotate(0deg); }
`;

const Wrapper = styled.div`
  align-items: center;
  animation: ${slideIn} 260ms cubic-bezier(0.2, 0.9, 0.3, 1.2);
  background: linear-gradient(135deg, #0f7534 0%, #164e19 100%);
  border: 1px solid #9dff8c;
  border-radius: 8px;
  bottom: 5rem;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  gap: 0.75rem;
  left: 50%;
  max-width: min(30rem, calc(100vw - 2rem));
  padding: 0.85rem 1rem;
  position: fixed;
  transform: translateX(-50%);
  z-index: 12000;

  /* Au-dessus de la barre de navigation mobile, qui occupe le bas de l'écran */
  @media screen and (max-width: 767px) {
    bottom: 4.5rem;
  }
`;

const Icon = styled.div`
  animation: ${pop} 420ms ease-out;
  flex: none;
  font-size: 1.6rem;
  line-height: 1;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-weight: bold;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const Message = styled.div`
  font-size: 0.85rem;
  opacity: 0.92;
`;

const CloseButton = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  display: flex;
  flex: none;
  opacity: 0.7;
  padding: 0;

  &:hover {
    opacity: 1;
  }
`;

export type ToastProps = {
  title: string;
  message?: ReactNode;
  /** Emoji ou icône affichée à gauche */
  icon?: ReactNode;
  /** Durée avant disparition automatique, en ms. 0 pour désactiver. */
  duration?: number;
  onClose: () => void;
};

export const Toast: FC<ToastProps> = ({
  title,
  message,
  icon = "🎉",
  duration = 5000,
  onClose,
}) => {
  // onClose est souvent une lambda recréée à chaque rendu : la garder dans une
  // ref évite de relancer le minuteur et de ne jamais fermer le toast.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!duration) return;
    const id = setTimeout(() => onCloseRef.current(), duration);
    return () => clearTimeout(id);
  }, [duration]);

  return (
    <Wrapper role="status" aria-live="polite">
      <Icon aria-hidden="true">{icon}</Icon>
      <Content>
        <Title>{title}</Title>
        {message && <Message>{message}</Message>}
      </Content>
      <CloseButton type="button" onClick={onClose} aria-label="Fermer">
        <MdClose />
      </CloseButton>
    </Wrapper>
  );
};

export default Toast;
